import { Dayjs } from 'dayjs'
import React from 'react'

import { ToggleButton } from '@/shared/components'
import { Checkbox } from '@/shared/components/Checkbox/Checkbox'
import { DatePicker } from '@/shared/components/DatePicker/DatePicker'
import { SelectOption } from '@/shared/components/DynamicForm/models/DynamicFieldData'
import { SelectDropdown } from '@/shared/components/SelectDropdown/SelectDropdown'
import { pxToRem } from '@/shared/styles/themes'
import { FormControlLabel, Stack, styled, ToggleButtonGroup, Typography } from '@mui/material'
import Grid from '@mui/material/Grid2'

import { DateRangeMode, FUTURE_PERIODS, OrderSearchFilters, PAST_PERIODS } from './libs/useOrderSearchData'

export type DateRangeFilterProps = {
  filters: OrderSearchFilters
  onFiltersChange: (filters: OrderSearchFilters) => void
  disabled?: boolean
}

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  filters,
  onFiltersChange,
  disabled,
}) => {
  const handleModeChange = (_: unknown, newMode: DateRangeMode | null) => {
    if (newMode) {
      onFiltersChange({ ...filters, mode: newMode })
    }
  }

  const handlePastChange = (_: React.SyntheticEvent, option: unknown) => {
    if (!option || typeof option !== 'object') return

    const days = (option as SelectOption).value as number

    onFiltersChange({ ...filters, pastDays: days })
  }

  const handleFutureChange = (_: React.SyntheticEvent, option: unknown) => {
    if (!option || typeof option !== 'object') return

    const days = (option as SelectOption).value as number

    onFiltersChange({ ...filters, futureDays: days })
  }

  const handleManualFromChange = (date: Dayjs | null) => {
    onFiltersChange({ ...filters, manualFromDate: date })
  }

  const handleManualToChange = (date: Dayjs | null) => {
    onFiltersChange({ ...filters, manualToDate: date })
  }

  const handleTodaysDateChange = (checked: boolean) => {
    onFiltersChange({
      ...filters,
      todaysDate: checked,
      manualFromDate: checked ? null : filters.manualFromDate,
      manualToDate: checked ? null : filters.manualToDate,
    })
  }

  return (
    <StyledContainer>
      <Stack spacing={2}>
        <StyledHeader>
          <Typography variant="subtitle2">Date Range</Typography>
          <ToggleButtonGroup
            value={filters.mode}
            exclusive
            onChange={handleModeChange}
            aria-label="date range selection mode"
          >
            <ToggleButton value="relative" primary text>
              Relative
            </ToggleButton>
            <ToggleButton value="manual" primary text>
              Manual
            </ToggleButton>
          </ToggleButtonGroup>
        </StyledHeader>

        {filters.mode === 'relative' ? (
          <Stack>
            <Grid container direction="row" alignItems="space-between" spacing={pxToRem(8)}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <SelectDropdown
                  label="Past Period"
                  value={PAST_PERIODS.find(opt => opt.value === filters.pastDays)}
                  onChange={handlePastChange}
                  options={PAST_PERIODS}
                  getOptionLabel={(option) => (option as SelectOption).label}
                  isOptionEqualToValue={(option, value) => (option as SelectOption).value === (value as SelectOption).value}
                  disabled={disabled || filters.todaysDate}
                  disablePortal={false}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <SelectDropdown
                  label="Future Period"
                  value={FUTURE_PERIODS.find(opt => opt.value === filters.futureDays)}
                  onChange={handleFutureChange}
                  options={FUTURE_PERIODS}
                  getOptionLabel={(option) => (option as SelectOption).label}
                  isOptionEqualToValue={(option, value) => (option as SelectOption).value === (value as SelectOption).value}
                  disabled={disabled || filters.todaysDate}
                  disablePortal={false}
                  fullWidth
                />
              </Grid>
            </Grid>

            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.todaysDate}
                  onChange={(e) => handleTodaysDateChange(e.target.checked)}
                  disabled={disabled}
                />
              }
              label="Today's Date"
            />
          </Stack>
        ) : (
          <Grid container direction="row" alignItems="space-between" spacing={pxToRem(8)}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DatePicker
                label="From"
                value={filters.manualFromDate}
                onChange={handleManualFromChange}
                disabled={disabled}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DatePicker
                label="To"
                value={filters.manualToDate}
                onChange={handleManualToChange}
                disabled={disabled}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                  },
                }}
              />
            </Grid>
          </Grid>
        )}
      </Stack>
    </StyledContainer>
  )
}

const StyledContainer = styled('div')`
  padding: ${pxToRem(8)};
  width: 80vw;

  ${(props) => props.theme.breakpoints.up('sm')} {
    width: ${pxToRem(420)};
  }
`

const StyledHeader = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
`